import { useNavigate } from "react-router-dom"
import { useState } from "react"
import type { FilterOptions, DropdownOption } from "~/components/filter-fields"
import type { FilterOptionsMap } from "~/components/makelovenotpornstars/stars-filtering-modal"
import type { SortOption } from "~/components/makelovenotpornstars/stars-sorting-menu"
import type { PrismaClient } from "@prisma/client"
import { UserAttributes, UserAttributeFilterType, UserAttributeTagType, Countries } from "./data"

export type UrlFilterParamsMap = {
  [key: string]: string[]
}

export type UrlParams = {
  parsedFilters: UrlFilterParamsMap
  parsedSortOption: string | null
}

export type SetFilter = (key: string, values: FilterOptions) => void

export const useFilters = () => {
  const [filters, setFilters] = useState<FilterOptionsMap>({})

  const setFilter: SetFilter = (key, values) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: values,
    }))
  }

  const handleClearAll = () => {
    setFilters({})
  }
  return {
    filters,
    setFilter,
    handleClearAll,
  }
}

export const mapUrlFilterParamsToFilterOptions = (filterParams: UrlFilterParamsMap): FilterOptionsMap => {
  const filterOptions: FilterOptionsMap = {}

  Object.keys(filterParams).forEach((key) => {
    const userAttribute = UserAttributes.find((attr) => attr.value === key)

    if (userAttribute) {
      if (userAttribute.type === UserAttributeFilterType.Dropdown) {
        filterOptions[key] = filterParams[key].map((value) => {
          let label: string

          if (key === "location") {
            label = Countries.find((country) => country.code === value)?.label || value
          } else if (key === "other") {
            label = value
          } else {
            const matchingOption = userAttribute.options.find((option) => option === value)
            label = matchingOption || value
          }

          return {
            value,
            label,
          } as DropdownOption
        })
      } else {
        filterOptions[key] = filterParams[key] as string[]
      }
    }
  })

  return filterOptions
}

export const parseUrlParams = (search: string): UrlParams => {
  const urlParams = new URLSearchParams(search)
  const parsedFilters: UrlFilterParamsMap = {}
  let parsedSortOption: string | null = null

  urlParams.forEach((value, key) => {
    if (key === "sort") {
      parsedSortOption = value
    } else if (key !== "page") {
      if (!parsedFilters[key]) {
        parsedFilters[key] = []
      }
      parsedFilters[key].push(value)
    }
  })

  return { parsedFilters, parsedSortOption }
}

export const applySortOption = (
  parsedSortOption: string | null,
  currentSortOption: SortOption,
  setSortOption: (option: SortOption) => void,
  sortingOptions: SortOption[]
): void => {
  if (parsedSortOption !== currentSortOption.value) {
    const newSortOption = sortingOptions.find((option) => option.value === parsedSortOption) || sortingOptions[0]
    setSortOption(newSortOption)
  }
}

export const applyFilters = (filters: FilterOptionsMap, setFilter: SetFilter): void => {
  Object.entries(filters).forEach(([key, values]) => {
    setFilter(key, values)
  })
}

export const useUpdateSortAndFilterParams = () => {
  const navigate = useNavigate()

  const updateUrlParams = (sortOption: SortOption, filters: FilterOptionsMap) => {
    const url = new URL(window.location.href)
    url.search = ""
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key].length > 0) {
        filters[key].forEach((value) => {
          const paramValue = typeof value === "string" ? value : value.value
          url.searchParams.append(key, paramValue)
        })
      }
    })
    url.searchParams.append("sort", sortOption.value)
    navigate(url.pathname + url.search, { preventScrollReset: true })
  }

  return updateUrlParams
}

export const useUpdateSortParam = () => {
  const navigate = useNavigate()

  const updateSortParam = (sortOption: SortOption) => {
    const url = new URL(window.location.href)
    const currentSortValue = url.searchParams.get("sort")
    if (currentSortValue) {
      url.searchParams.delete("sort")
    }
    url.searchParams.append("sort", sortOption.value)
    navigate(url.pathname + url.search, { preventScrollReset: true })
  }

  return updateSortParam
}

export function getCountryCode(country: string): string | null {
  const countryData = Countries.find((c) => c.label === country || c.code === country)
  return countryData ? countryData.code : null
}

export const getCountryName = (code: string): string | null => {
  const countryData = Countries.find((c) => c.code === code)
  return countryData ? countryData.label : null
}

export const getUserLocations = async (db: PrismaClient) => {
  const distinctCountries = await db.tags.findMany({
    distinct: ["Name"],
    where: {
      Name: { not: null },
      UserAttributeTagTypeID: UserAttributeTagType.Location,
    },
  })
  return distinctCountries.map((tag) => getCountryCode(tag.Name as string))
}
